import { Dispatch, SetStateAction } from 'react';
import http from '../shared/HttpCommon';
import { CanceledError } from 'axios';
import { ITrustpageFilter } from '../shared/interfaces/ITrustpageFilter';
import { ITrustpageEntry } from '../shared/interfaces/ITrustpageEntry';

const addTrustpage = async (
  companyName: string,
  companyWebsiteUrl: string,
  monitorings: { id: string; type: string }[],
  companyLogo: string,
  primaryColor: string,
  secondaryColor: string,
  customFont: string,
  customFontFormat: string,
  aboutCompanyText: string,
  copyrightText: string,
  privacyPolicyLink: string,
  imprintLink: string,
  contactEmail: string,
  lang: string
) => {
  try {
    const response = await http.post('/trustpages', {
      companyName,
      companyWebsiteUrl,
      monitorings,
      companyLogo,
      primaryColor,
      secondaryColor,
      customFont,
      customFontFormat,
      aboutCompanyText,
      copyrightText,
      privacyPolicyLink,
      imprintLink,
      contactEmail,
      lang,
    });
    return response.status === 200;
  } catch (error) {
    console.error(error);
    return false;
  }
};

const getTrustpage = async (
  trustpageId: string,
  loading: Dispatch<SetStateAction<boolean>>,
  signal: AbortSignal
) => {
  try {
    loading(true);
    const { data } = await http.get(`/trustpages/${trustpageId}`, { signal });
    return data;
  } catch (error) {
    if (error instanceof CanceledError) {
    } else {
      console.error(error);
    }
  } finally {
    loading(false);
  }
};

const getTrustpagePublic = async (
  trustpageId: string,
  loading: Dispatch<SetStateAction<boolean>>,
  signal: AbortSignal
) => {
  try {
    loading(true);
    const { data } = await http.get(`/trustpages/public/${trustpageId}`, { signal });
    return data;
  } catch (error) {
    if (error instanceof CanceledError) {
    } else {
      console.error(error);
    }
  } finally {
    loading(false);
  }
};

const getTrustpageReviewsPublic = async (
  trustpageId: string,
  loading: Dispatch<SetStateAction<boolean>>,
  signal: AbortSignal,
  filter: ITrustpageFilter
) => {
  try {
    loading(true);
    const { data } = await http.post(`/trustpages/public/${trustpageId}/reviews`, filter, {
      signal,
    });
    return data;
  } catch (error) {
    if (error instanceof CanceledError) {
    } else {
      console.error(error);
    }
  } finally {
    loading(false);
  }
};

const getTrustpages = async (loading: Dispatch<SetStateAction<boolean>>, signal: AbortSignal) => {
  try {
    loading(true);
    const { data } = await http.get('/trustpages', { signal });
    return data;
  } catch (error) {
    if (!(error instanceof CanceledError)) {
      console.error(error);
    }
  } finally {
    loading(false);
  }
};

const deleteTrustpage = async (id: string) => {
  try {
    const { data } = await http.delete(`/trustpages/${id}`);
    const { success } = data;
    return success;
  } catch (error) {
    console.error(error);
    return false;
  }
};

const updateTrustpage = async (pageId: string, TrustPAge: ITrustpageEntry) => {
  try {
    const { data } = await http.put(`/trustpages/${pageId}`, TrustPAge);
    const { success } = data;
    return success;
  } catch (error) {
    console.error(error);
    return false;
  }
};

const getTrustpageSeal = async (sealID: string) => {
  try {
    const response = await http.get(`/trustpages/seal/${sealID}`, {
      responseType: 'blob',
    });
    return response.data;
  } catch (error) {
    console.error(error);
    return false;
  }
};

const TrustpageService = {
  addTrustpage,
  getTrustpage,
  getTrustpagePublic,
  getTrustpageReviewsPublic,
  getTrustpages,
  deleteTrustpage,
  updateTrustpage,
  getTrustpageSeal,
};

export default TrustpageService;
